import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { DataLayer } from '../lib/dataLayer'

interface DayActivity {
  date: string
  count: number
}

interface MasteryStats {
  total: number
  mastered: number
  inProgress: number
  notStarted: number
  byLevel: {
    level: number
    count: number
  }[]
}

export function Stats() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Data states
  const [monthActivity, setMonthActivity] = useState<DayActivity[]>([])
  const [masteryStats, setMasteryStats] = useState<MasteryStats>({
    total: 0,
    mastered: 0,
    inProgress: 0,
    notStarted: 0,
    byLevel: []
  })

  useEffect(() => {
    if (user) {
      loadStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const loadStats = async () => {
    try {
      setLoading(true)
      
      // Load cards and progress data
      const cards = await DataLayer.getCards()
      const progress = await DataLayer.getUserCardProgress(user!.id)
      
      // Create progress map
      const progressMap = new Map(
        progress.map(p => [p.card_id, p])
      )
      
      // Calculate mastery statistics
      let mastered = 0
      let notStarted = 0
      const levelCounts = new Map<number, number>()
      
      cards.forEach(card => {
        const cardProgress = progressMap.get(card.id)
        
        if (!cardProgress || cardProgress.review_count === 0) {
          notStarted++
        } else if (cardProgress.status === 'mastered') {
          mastered++
        }
        
        // Count by mastery level
        const masteryScore = cardProgress?.mastery_score || 0
        const level = Math.floor(masteryScore / 20) // 0-19: 0, 20-39: 1, etc.
        levelCounts.set(level, (levelCounts.get(level) || 0) + 1)
      })
      
      const inProgress = cards.length - mastered - notStarted
      
      // Convert level counts to array
      const byLevel = Array.from(levelCounts.entries())
        .map(([level, count]) => ({ level, count }))
        .sort((a, b) => a.level - b.level)
      
      setMasteryStats({
        total: cards.length,
        mastered,
        inProgress,
        notStarted,
        byLevel
      })
      
      // Calculate daily activity for the past 30 days
      const today = new Date()
      const thirtyDaysAgo = new Date(today)
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29)
      
      // Get study sessions for activity calendar
      // For now, we'll use progress update timestamps as a proxy for activity
      const dailyCounts = new Map<string, number>()
      
      progress.forEach(p => {
        if (p.last_reviewed) {
          const date = new Date(p.last_reviewed)
          if (date >= thirtyDaysAgo) {
            const dateKey = date.toISOString().split('T')[0]
            dailyCounts.set(dateKey, (dailyCounts.get(dateKey) || 0) + 1)
          }
        }
      })
      
      // Create array of all days in the past 30 days
      const activityData: DayActivity[] = []
      for (let i = 0; i < 30; i++) {
        const date = new Date(today)
        date.setDate(date.getDate() - i)
        const dateKey = date.toISOString().split('T')[0]
        activityData.unshift({
          date: dateKey,
          count: dailyCounts.get(dateKey) || 0
        })
      }
      
      setMonthActivity(activityData)
      
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Activity calendar component
  const ActivityCalendar = () => {
    const getColorIntensity = (count: number) => {
      if (count === 0) return 'bg-gray-100'
      if (count <= 5) return 'bg-green-200'
      if (count <= 10) return 'bg-green-300'
      if (count <= 20) return 'bg-green-400'
      if (count <= 30) return 'bg-green-500'
      return 'bg-green-600'
    }
    
    // Group by weeks
    const weeks: DayActivity[][] = []
    let currentWeek: DayActivity[] = []
    
    // Add padding for the first week
    const firstDay = new Date(monthActivity[0]?.date || new Date())
    const startPadding = firstDay.getDay()
    for (let i = 0; i < startPadding; i++) {
      currentWeek.push({ date: '', count: -1 })
    }
    
    monthActivity.forEach((day, index) => {
      currentWeek.push(day)
      if (currentWeek.length === 7) {
        weeks.push(currentWeek)
        currentWeek = []
      }
    })
    
    // Add padding for the last week
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push({ date: '', count: -1 })
      }
      weeks.push(currentWeek)
    }
    
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Activity (Past 30 Days)</h3>
        <div className="flex gap-1">
          <div className="flex flex-col gap-1 text-xs text-gray-500 mr-2">
            <div className="h-4">Sun</div>
            <div className="h-4">Mon</div>
            <div className="h-4">Tue</div>
            <div className="h-4">Wed</div>
            <div className="h-4">Thu</div>
            <div className="h-4">Fri</div>
            <div className="h-4">Sat</div>
          </div>
          <div className="flex gap-1">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-1">
                {week.map((day, dayIndex) => (
                  <div
                    key={dayIndex}
                    className={`w-4 h-4 rounded-sm ${
                      day.count === -1 
                        ? '' 
                        : getColorIntensity(day.count)
                    }`}
                    title={day.date && day.count >= 0 ? `${day.date}: ${day.count} reviews` : ''}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-gray-600">
          <span>Less</span>
          <div className="flex gap-1">
            <div className="w-3 h-3 bg-gray-100 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-200 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-300 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-400 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
            <div className="w-3 h-3 bg-green-600 rounded-sm"></div>
          </div>
          <span>More</span>
        </div>
      </div>
    )
  }

  // Simple pie chart component
  const PieChart = () => {
    const { mastered, inProgress, notStarted, total } = masteryStats
    if (total === 0) return null
    
    const masteredPercent = (mastered / total) * 100
    const inProgressPercent = (inProgress / total) * 100
    const notStartedPercent = (notStarted / total) * 100
    
    // Simple CSS-based pie chart
    const conicGradient = `conic-gradient(
      #10b981 0deg ${masteredPercent * 3.6}deg,
      #3b82f6 ${masteredPercent * 3.6}deg ${(masteredPercent + inProgressPercent) * 3.6}deg,
      #e5e7eb ${(masteredPercent + inProgressPercent) * 3.6}deg 360deg
    )`
    
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Card Mastery Distribution</h3>
        <div className="flex items-center gap-8">
          <div 
            className="relative w-48 h-48 rounded-full"
            style={{ background: conicGradient }}
          >
            <div className="absolute inset-4 bg-white rounded-full flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl font-bold">{total}</div>
                <div className="text-sm text-gray-500">Total Cards</div>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <div>
                <div className="font-medium">Mastered</div>
                <div className="text-sm text-gray-500">{mastered} ({masteredPercent.toFixed(1)}%)</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <div>
                <div className="font-medium">In Progress</div>
                <div className="text-sm text-gray-500">{inProgress} ({inProgressPercent.toFixed(1)}%)</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 bg-gray-200 rounded"></div>
              <div>
                <div className="font-medium">Not Started</div>
                <div className="text-sm text-gray-500">{notStarted} ({notStartedPercent.toFixed(1)}%)</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Mastery level bar chart
  const MasteryLevelChart = () => {
    const maxCount = Math.max(...masteryStats.byLevel.map(l => l.count), 1)
    
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Mastery Score Distribution</h3>
        <div className="space-y-3">
          {[0, 1, 2, 3, 4, 5].map(level => {
            const count = masteryStats.byLevel.find(l => l.level === level)?.count || 0
            const percent = (count / maxCount) * 100
            
            return (
              <div key={level} className="flex items-center gap-3">
                <div className="w-20 text-sm text-gray-600">
                  {level * 20}-{level * 20 + 19}%
                </div>
                <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-yellow-400 to-green-500 rounded-full"
                    style={{ width: `${percent}%` }}
                  />
                  <div className="absolute inset-0 flex items-center px-2">
                    <span className="text-xs font-medium">{count}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (loading) {
    return <div className="text-center py-4">Loading statistics...</div>
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-800">{error}</p>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Statistics</h2>
      
      <ActivityCalendar />
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PieChart />
        <MasteryLevelChart />
      </div>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-gray-900">{masteryStats.total}</div>
          <div className="text-sm text-gray-500">Total Cards</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">{masteryStats.mastered}</div>
          <div className="text-sm text-gray-500">Mastered</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">{masteryStats.inProgress}</div>
          <div className="text-sm text-gray-500">In Progress</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-gray-400">{masteryStats.notStarted}</div>
          <div className="text-sm text-gray-500">Not Started</div>
        </div>
      </div>
    </div>
  )
}